import {useCallback, useState} from 'react';

import FolderDAOCache from 'file-browser/FolderDAOCache';
import FileStates from 'FileStates';
import {IGNORE_FIELD_ID} from 'spreadsheet-import-wizard/shared/ColumnMapper';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';

import type {ColumnMapping} from './moleculeImportDAO';
import {bulkImportMoleculesFromSpreadsheet} from './moleculeImportDAO';
import type {ModalAction, ModalState} from './moleculeImportReducer';
import {downloadErrorsAsCsv, pollBulkTaskUntilDone} from './pollBulkTask';

const BUILT_IN_SENTINELS: Record<string, string> = {
  __molecule_name__: 'NAME',
  __molecule_structure_smiles__: 'SMILES',
  __molecule_structure_molfile__: 'MOLFILE',
  __molecule_alias__: 'ALIAS',
};

export default function useSubmitImport(
  state: ModalState,
  dispatch: React.Dispatch<ModalAction>,
  close: () => void
): {handleSubmit: () => void; isSubmitting: boolean} {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!state.s3Url || !state.registrationConfig || !state.schemaId) {
      return;
    }
    setIsSubmitting(true);
    dispatch({type: 'GO_TO_STEP', payload: {step: 'SUBMITTING'}});

    const columnMappings: Array<ColumnMapping> = [];
    for (const [columnName, fieldId] of Object.entries(state.columnMappings)) {
      if (fieldId === IGNORE_FIELD_ID) {
        continue;
      }
      const role = BUILT_IN_SENTINELS[fieldId];
      if (role) {
        columnMappings.push({type: 'builtIn', columnName, role});
      } else {
        const expandedKeys = state.fieldExpansion[fieldId] ?? [fieldId];
        for (const key of expandedKeys) {
          columnMappings.push({type: 'schemaField', columnName, schemaFieldKey: key});
        }
      }
    }

    const folderId = state.registrationConfig.folderId;

    try {
      const response = await bulkImportMoleculesFromSpreadsheet({
        s3Url: state.s3Url,
        columnMappings,
        registrationConfig: {
          registryId: state.registrationConfig.registryId,
          namingStrategy: state.registrationConfig.namingStrategy,
          folderId,
          schemaId: state.schemaId,
          compoundSchemaId: state.compoundSchemaId,
        },
      });

      close();

      const trayTaskId = TrayTaskActions.createTrayTask({
        heading: 'Importing molecules...',
        subheading: `${response.successCount} molecules queued for import`,
        progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
      });

      pollBulkTaskUntilDone(response.pollingUri, (status) => {
        const {totalRecords, numberProcessed} = status.progress;
        if (totalRecords > 0 && numberProcessed > 0) {
          const pct = Math.min(Math.round((numberProcessed / totalRecords) * 100), 99);
          TrayTaskActions.updateTrayTask(trayTaskId, {
            progress: pct,
            subheading: `Processing ${numberProcessed} of ${totalRecords} molecules...`,
          });
        }
      })
        .then((finalStatus) => {
          const {successCount, errorCount} = finalStatus.result;
          FolderDAOCache.invalidateFolderContents(folderId);

          if (successCount > 0) {
            TrayTaskActions.updateTrayTask(trayTaskId, {
              progress: TrayTaskConstants.SUCCESS_PROGRESS,
              subheading: `Imported ${successCount} molecules to folder`,
              actions: [
                {
                  actionText: 'Open Folder',
                  onActionClick: () => {
                    FileStates.get().openFolder(folderId);
                  },
                },
              ],
            });
          }

          if (errorCount > 0) {
            if (successCount === 0) {
              TrayTaskActions.updateTrayTask(trayTaskId, {
                progress: TrayTaskConstants.FAILURE_PROGRESS,
                error:
                  'Molecules that could not be imported have been saved to a spreadsheet. Check your email, or download the spreadsheet from here.',
                actions: [
                  {
                    actionText: 'Download Report',
                    onActionClick: () => {
                      downloadErrorsAsCsv(finalStatus.taskId);
                    },
                  },
                ],
              });
            } else {
              const errorTaskId = TrayTaskActions.createTrayTask({
                heading: 'Some molecules could not be imported',
                progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
              });
              TrayTaskActions.updateTrayTask(errorTaskId, {
                progress: TrayTaskConstants.FAILURE_PROGRESS,
                error:
                  'Molecules that could not be imported have been saved to a spreadsheet. Check your email, or download the spreadsheet from here.',
                actions: [
                  {
                    actionText: 'Download Report',
                    onActionClick: () => {
                      downloadErrorsAsCsv(finalStatus.taskId);
                    },
                  },
                ],
              });
            }
          }
        })
        .catch(() => {
          TrayTaskActions.updateTrayTask(trayTaskId, {
            progress: TrayTaskConstants.FAILURE_PROGRESS,
            error: 'An error occurred while tracking import progress.',
          });
        });
    } catch (error: unknown) {
      setIsSubmitting(false);
      const taskError = error as {isBenchlingTaskError?: boolean; responseJSON?: {message?: string}};
      if (taskError.isBenchlingTaskError || taskError.responseJSON) {
        const message =
          (taskError.responseJSON as {message?: string})?.message ??
          'An unexpected error occurred while submitting the import. Please try again.';
        dispatch({type: 'SET_ERROR_MESSAGE', payload: {errorMessage: message}});
        dispatch({type: 'GO_TO_STEP', payload: {step: 'FINALIZE_IMPORT'}});
      } else {
        throw error;
      }
    }
  }, [state, dispatch, close]);

  return {handleSubmit, isSubmitting};
}
